/**
 * The two hearts (DECISION-37).
 *
 * ===========================================================================
 * ⚠️ A ROW CARRIES TWO OF THEM AND THEY DO DIFFERENT THINGS
 * ===========================================================================
 *
 *   `KarmaBadge`      FILLED heart + a number, beside the character's name.
 *                     Drawn when there is karma, and on YOUR OWN row whatever
 *                     the count. Opens the HISTORY — «за что».
 *   `KarmaRowMenu`    three dots at the right-hand end of the row, inside the
 *                     LAST column rather than in one of its own. Holds «Плюс в
 *                     карму» and every future row action.
 *
 * ⚠️ THE GIVE ACTION HAD ITS OWN COLUMN TWICE AND SHOULD NOT HAVE HAD ONE AT
 * ALL (owner's call, twice). First a 44px button column — measured, that made
 * the table fit only from 375px up and overflow at every width with a long
 * name, with the action as the last column, i.e. the part you cannot see. Then
 * a 40px menu column, which was the same mistake one size smaller. A row action
 * is not a kind of data and does not get a column; it rides along at the end of
 * the last one there is.
 *
 * ⚠️ THE BADGE IS A CHIP THAT HAPPENS TO BE A BUTTON, and it is built out of
 * `chipVariants` rather than out of literal colours. The owner asked for the
 * «вы» label's colours, and «вы» is `<Chip variant="soft" color="accent">` — so
 * this renders the SAME class list on a real `<button>`. Copying the two hex
 * values instead would be a third opinion about the accent that stops tracking
 * the theme the moment either token moves, and this app's two themes are
 * deliberately different hexes.
 *
 * ⚠️ ONE MODAL PER PAGE, NOT PER ROW. Both dialogs are mounted by the LIST from
 * a single piece of state naming the character, because a modal per row is
 * forty modals in the DOM of a page whose whole job is to render a table.
 */

import { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Button,
  chipVariants,
  Dropdown,
  Label,
  Modal,
  Separator,
  TextArea,
  TextField,
} from '@heroui/react';
import { MAX_KARMA_MESSAGE_CHARS } from '@vlastiq/contracts';
import {
  adminDeleteKarma,
  fetchKarma,
  giveKarma,
  type KarmaEntry,
  type KarmaResponse,
} from '../data/api.ts';
import { ActionButton } from './ActionButton.tsx';
import { IconButton } from './IconButton.tsx';
import { InlineLoader } from './InlineLoader.tsx';
import { DotsIcon, KarmaIcon, TrashIcon } from './icons.tsx';
import { errorText } from '../lib/errors.ts';
import {
  karmaBadgeLabel,
  karmaDateLabel,
  karmaGiverLabel,
  shouldShowKarma,
  type KarmaControl,
} from '../lib/karma.ts';
import { useAction } from '../lib/useAction.ts';

/** Which character a dialog is about. Carries the name so it can be shown. */
export interface KarmaTarget {
  characterId: number;
  characterName: string;
}

/* --------------------------------------------------------------- the badge */

/** The count beside a character's name, as a button onto the history. */
export function KarmaBadge({
  karma,
  characterName,
  isOwn,
  onOpen,
}: {
  karma: number;
  characterName: string;
  /** The viewer's own row, which is the one exception to hiding a zero. */
  isOwn: boolean;
  onOpen: () => void;
}) {
  // Hidden at zero, except on your own row. The rule is `shouldShowKarma`
  // rather than a condition here because it has been wrong twice; see it.
  if (!shouldShowKarma(karma, isOwn)) return null;

  /*
   * ⚠️ `chipVariants(...)` RETURNS SLOTS, NOT A STRING, and interpolating it
   * into a template literal yields `[object Object]` — a button with NO chip
   * classes at all. That shipped in the first draft and was invisible to
   * TypeScript, to the build and to every test: it rendered as an unstyled
   * button whose `size-3` SVG, made `display: block` by Tailwind's preflight,
   * pushed the number onto a second line. Caught by rendering it in a headless
   * browser and measuring the box — 12×35 where a chip is about 40×20.
   *
   * `.base()` and `.label()` are the same two slots HeroUI's own `Chip` uses,
   * which is what keeps this identical to the «вы» label beside it.
   */
  const chip = chipVariants({ size: 'sm', variant: 'soft', color: 'accent' });

  return (
    <button
      type="button"
      onClick={onOpen}
      /*
        `shrink-0` matches the «вы» chip beside it: this sits in a flex row with
        a truncating name, and without it a long character name squeezes the
        count to nothing rather than truncating itself.

        `cursor-pointer` is explicit because a `<button>` does not get one by
        default in Tailwind's reset, and this one has to read as pressable —
        it is the only way into the history.
      */
      className={`${chip.base()} shrink-0 cursor-pointer`}
      aria-label={karmaBadgeLabel(characterName, karma)}
    >
      {/* Sized here rather than in `icons.tsx`: this is not inside a HeroUI
          `Button`, so `.button svg`'s size-5/size-4 rule does not reach it. */}
      <KarmaIcon className="size-3" aria-hidden="true" />
      <span className={chip.label()}>{karma}</span>
    </button>
  );
}

/* -------------------------------------------------------------- the action */

/**
 * The row's actions, behind three dots, at the right-hand end of the row.
 *
 * ===========================================================================
 * ⚠️ IT LIVES INSIDE THE LAST COLUMN AND MUST NOT BE GIVEN ONE OF ITS OWN
 * ===========================================================================
 *
 * It has had a column twice now and both were wrong (owner's call, twice).
 * First a 44px button column: measured, that made the table fit only from 375px
 * up and overflow at every phone width with a long name — with the action as
 * the LAST column, i.e. the part you cannot see. Then a 40px menu column, which
 * was the same mistake one size smaller.
 *
 * A row action is not a kind of DATA. Every column here answers a question about
 * a character — who, what role, what level — and a column of controls answers
 * none, while costing the same horizontal space as one that does. It rides at
 * the end of the last real column instead, which is also where «to the right of
 * each character» actually is.
 *
 * ⚠️ SIZED 32px, LIKE THE LOOT QUEUE'S ROW MENU — the documented exception to
 * the 44px thumb rule for a dense table, and the reason the two tables' rows do
 * not read as different kinds of thing.
 */
export function KarmaRowMenu({
  control,
  characterName,
  isDisabled,
  onGive,
}: {
  control: KarmaControl;
  characterName: string;
  isDisabled?: boolean;
  onGive: () => void;
}) {
  // Nothing to offer yet on your own row. When a second action lands, this
  // guard is what it removes.
  if (control.kind === 'own') return null;

  return (
    <Dropdown>
      <Dropdown.Trigger
        aria-label={`Действия — ${characterName}`}
        isDisabled={isDisabled}
        className="inline-flex size-8 items-center justify-center rounded-[var(--control-radius)] text-muted hover:bg-default"
      >
        <DotsIcon className="size-4" aria-hidden="true" />
      </Dropdown.Trigger>
      {/*
        Safe inside `Table.ScrollContainer` even though that is `overflow-x:
        auto`: react-aria's `Popover` portals to the document body, so the menu
        is not a descendant of the clipping box.
      */}
      <Dropdown.Popover>
        <Dropdown.Menu>
          {/*
            ⚠️ DISABLED RATHER THAN ABSENT WHEN TODAY'S POINTS ARE SPENT, and
            the label says which. An item that vanishes for a day reads as a
            feature that broke; `control.hint` carries «на сегодня очки кармы
            для него кончились», which is the whole explanation.

            `onAction`, not `onPress` — a menu item is react-aria's `MenuItem`,
            which fires `onAction` on click, Enter, Space or typeahead.
          */}
          <Dropdown.Item
            aria-label={`${control.hint} — ${characterName}`}
            isDisabled={control.kind === 'spent'}
            onAction={onGive}
          >
            {control.kind === 'spent' ? 'Плюс в карму — на сегодня всё' : 'Плюс в карму'}
          </Dropdown.Item>
        </Dropdown.Menu>
      </Dropdown.Popover>
    </Dropdown>
  );
}

/* ---------------------------------------------------------- the give dialog */

/**
 * «Плюс в карму» — one point, with an optional word about why.
 *
 * ⚠️ CLOSES ONLY ON SUCCESS. A refusal keeps the panel and the typed text, for
 * the reason Админка → Знания already carries: discarding what somebody has
 * just been told did not save leaves them to type it again.
 */
export function KarmaGiveDialog({
  target,
  remaining,
  onClose,
  onGranted,
}: {
  target: KarmaTarget;
  /** How many the viewer may still give today. Shown, never enforced here. */
  remaining: number;
  onClose: () => void;
  onGranted: (result: { characterId: number; total: number; remainingToday: number }) => void;
}) {
  const [message, setMessage] = useState('');
  const action = useAction();

  function submit() {
    void action.run(
      'give',
      async () => {
        const result = await giveKarma(target.characterId, message.trim() || null);
        onGranted(result);
        onClose();
      },
      {
        success: `Плюс в карму: ${target.characterName}.`,
        /*
          A fallback only. All three refusals arrive as a 409 carrying the
          server's own sentence, and they need different next steps — «себе
          нельзя» never becomes true, «на сегодня всё» does tomorrow. `errorText`
          prefers the server's message; see lib/errors.ts.
        */
        error: 'Не удалось поставить плюс.',
      },
    );
  }

  return (
    <KarmaModal title={`Плюс в карму — ${target.characterName}`} onClose={onClose}>
      <Modal.Body className="flex flex-col gap-3">
        <TextField value={message} onChange={setMessage} fullWidth>
          <Label>За что (необязательно)</Label>
          <TextArea rows={3} maxLength={MAX_KARMA_MESSAGE_CHARS} />
        </TextField>
        {/* Said before the press, not after it. Learning you had nothing left
            AFTER writing a thank-you is the failure this line exists to
            prevent — the count comes down with the roster for that reason. */}
        <p className="text-sm text-muted">
          Осталось сегодня для этого персонажа: {remaining}. Снять плюс нельзя.
        </p>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="ghost" isDisabled={action.isBusy} onPress={onClose}>
          Отмена
        </Button>
        <ActionButton isPending={action.isPending('give')} isDisabled={action.isBusy} onPress={submit}>
          Поставить плюс
        </ActionButton>
      </Modal.Footer>
    </KarmaModal>
  );
}

/* ------------------------------------------------------- the history dialog */

/**
 * Who gave a character karma, and why.
 *
 * ⚠️ THE LOAD'S FAILURE IS AN INLINE `Alert`, NOT A TOAST. It explains why the
 * list underneath it is empty and has to stay on screen; a toast leaves after
 * eight seconds and the reader is then looking at a blank dialog with no
 * account of why. The DELETE's outcome is a toast, because it is news about
 * something that just happened. That split is `lib/notify.tsx`'s whole rule.
 */
export function KarmaHistoryDialog({
  target,
  isAdmin,
  onClose,
  onChanged,
}: {
  target: KarmaTarget;
  isAdmin: boolean;
  onClose: () => void;
  /** Fired when an admin removed an entry, so the roster's total can follow. */
  onChanged: (characterId: number, total: number) => void;
}) {
  const [data, setData] = useState<KarmaResponse | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  /** The entry whose delete has been pressed once. */
  const [armedId, setArmedId] = useState<number | null>(null);
  const action = useAction();

  const load = useCallback(async () => {
    try {
      setData(await fetchKarma(target.characterId));
      setLoadError(null);
    } catch (caught) {
      setLoadError(errorText(caught, 'Не удалось загрузить историю кармы.'));
    }
  }, [target.characterId]);

  useEffect(() => {
    void load();
  }, [load]);

  /**
   * ⚠️ TWO PRESSES, AND THE SECOND IS A WORDED `danger` BUTTON. The resting
   * state is a glyph; a bin is a fine way to ASK and a terrible way to CONFIRM.
   * This one is not undoable and it lowers a number other people can see.
   */
  function remove(entry: KarmaEntry) {
    if (armedId !== entry.id) {
      setArmedId(entry.id);
      return;
    }

    void action.run(
      `delete:${entry.id}`,
      async () => {
        await adminDeleteKarma(entry.id);
        setArmedId(null);
        const refreshed = await fetchKarma(target.characterId);
        setData(refreshed);
        onChanged(target.characterId, refreshed.total);
      },
      { success: 'Запись удалена.', error: 'Не удалось удалить запись.' },
    );
  }

  return (
    <KarmaModal title={`Карма — ${target.characterName}`} onClose={onClose}>
      <Modal.Body className="flex flex-col gap-3">
        {loadError && <Alert status="danger">{loadError}</Alert>}
        {data === null && !loadError && <InlineLoader />}

        {data && (
          <>
            <p className="text-sm text-muted">Всего очков: {data.total}.</p>

            {data.entries.length === 0 && (
              <p className="text-sm text-muted">Пока никто не ставил.</p>
            )}

            <ul className="flex flex-col gap-3">
              {data.entries.map((entry) => (
                <li key={entry.id} className="flex flex-col gap-1">
                  <div className="flex flex-wrap items-baseline gap-2">
                    <span className="font-medium">{karmaGiverLabel(entry)}</span>
                    <span className="text-xs text-muted">{karmaDateLabel(entry.grantedAt)}</span>
                    {/* Only the bot path went through name resolution, so only
                        it can have landed on the wrong person. An admin
                        reviewing a suspect entry needs to know which kind they
                        are looking at. */}
                    {entry.source === 'bot' && (
                      <span className="text-xs text-muted">· из чата</span>
                    )}

                    {isAdmin && armedId !== entry.id && (
                      <span className="ml-auto">
                        <IconButton
                          label="Удалить"
                          subject={`${karmaGiverLabel(entry)}, ${karmaDateLabel(entry.grantedAt)}`}
                          icon={<TrashIcon />}
                          tone="danger"
                          isDisabled={action.isBusy}
                          onPress={() => remove(entry)}
                        />
                      </span>
                    )}
                  </div>

                  {entry.message ? (
                    <p className="text-sm">{entry.message}</p>
                  ) : (
                    /* Said outright rather than left blank: a missing line where
                       every other row has one reads as data that failed to
                       load, and «без комментария» is a real, common state. */
                    <p className="text-sm text-muted">без комментария</p>
                  )}

                  {isAdmin && armedId === entry.id && (
                    <div className="flex flex-wrap items-center gap-2">
                      <ActionButton
                        variant="danger"
                        size="sm"
                        isPending={action.isPending(`delete:${entry.id}`)}
                        isDisabled={action.isBusy}
                        onPress={() => remove(entry)}
                      >
                        Удалить запись
                      </ActionButton>
                      <Button
                        variant="ghost"
                        size="sm"
                        isDisabled={action.isBusy}
                        onPress={() => setArmedId(null)}
                      >
                        Отмена
                      </Button>
                      {/* Stated at the moment it matters: this is not a
                          downvote, and it is the only act in the app that
                          lowers a number other people can see. */}
                      <p className="w-full text-sm text-muted">
                        Очко пропадёт у персонажа, а не вернётся тому, кто его поставил.
                      </p>
                    </div>
                  )}

                  <Separator className="mt-2" />
                </li>
              ))}
            </ul>
          </>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="ghost" onPress={onClose}>
          Закрыть
        </Button>
      </Modal.Footer>
    </KarmaModal>
  );
}

/* ------------------------------------------------------------------ shared */

/**
 * The dialog shell both karma modals use.
 *
 * ⚠️ CONTROLLED AND ALWAYS OPEN, WITH NO `Modal.Trigger`. The caller mounts this
 * only while a target is selected, so `isOpen` is a constant and closing is the
 * caller unmounting it. A `Modal.Trigger` would be wrong twice over: it renders
 * `<div role="button">`, and both of this app's karma triggers are already real
 * `<button>`s — nesting them is the interactive-inside-interactive problem
 * `Popover.Trigger` already carries a note about.
 *
 * Focus still returns to whatever opened it: react-aria's `FocusScope` captures
 * `document.activeElement` when the scope mounts, not a trigger ref.
 */
function KarmaModal({
  title,
  onClose,
  children,
}: {
  title: string;
  onClose: () => void;
  children: React.ReactNode;
}) {
  return (
    <Modal
      isOpen
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
    >
      <Modal.Backdrop>
        <Modal.Container placement="center" size="md">
          <Modal.Dialog>
            <Modal.Header>
              <Modal.Heading>{title}</Modal.Heading>
            </Modal.Header>
            {children}
          </Modal.Dialog>
        </Modal.Container>
      </Modal.Backdrop>
    </Modal>
  );
}
